import { Form, Input, Button, message, Upload, Spin } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import drEasyApi from "../../../Api/Customapi";
import { getImg } from "../../../Api/getImg";
import { UploadOutlined } from "@ant-design/icons";
import getErrorMessage from "../../../hooks/getErrorMssg";

const useGetCenterData = () => {
    return useQuery({
        queryKey: ["centerData"],
        queryFn: async () => {
            const response = await drEasyApi.get("/api/Centers/GetmyCenter");
            return response.data;
        },
    });
};

const PreviewField = ({ label, value, dir }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</span>
        <span
            dir={dir}
            className="text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 min-h-[38px] break-words"
        >
            {value || <span className="text-gray-300">—</span>}
        </span>
    </div>
);

// Social media config for DRY rendering
const SOCIAL_FIELDS = [
    { name: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourpage" },
    { name: "facebook",  label: "Facebook",  placeholder: "https://facebook.com/yourpage"  },
    { name: "twitter",   label: "Twitter / X", placeholder: "https://x.com/yourhandle"    },
    { name: "linkedIn",  label: "LinkedIn",  placeholder: "https://linkedin.com/company/yourpage" },
    { name: "youtube",  label: "youtube",  placeholder: "https://youtube.com/company/yourpage" },
    { name: "whatsApp",  label: "Whatsapp",  placeholder: "+20 0000000000" },
];

const EditCenterScreen = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const [logoIdPreview, setLogoIdPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const { data: centerData, isLoading } = useGetCenterData();

    useEffect(() => {
        const role = localStorage.getItem("role");
        setDisabled(role !== "3");
    }, []);

    useEffect(() => {
        if (centerData) {
            form.setFieldsValue({
                centerId:            centerData.id,
                name:                centerData.name,
                nameAr:              centerData.nameAr,
                logoId:              centerData.logoId,
                detailedAddress:     centerData.address,
                detailedAddressAr:   centerData.addressAr,
                locationUrl:         centerData.locationUrl,
                availabilityNote:    centerData.avilibiltyNote,
                contactPhone:        centerData.contactPhone,
                contactEmail:        centerData.contactEmail,
                // Social media
                instagram:           centerData.instagram,
                facebook:            centerData.facebook,
                twitter:             centerData.twitter,
                linkedIn:            centerData.linkedIn,
                whatsApp:            centerData.whatsApp,
                youtube:            centerData.youtube,
            });
            setLogoIdPreview(centerData.logoId);
        }
    }, [centerData, form]);

    const { mutate, isPending } = useMutation({
        mutationFn: async (values) => {
            await drEasyApi.put("/api/Centers/Edit", values);
        },
        onSuccess: () => {
            message.success("Center updated successfully");
            queryClient.invalidateQueries(["centerData"]);
        },
        onError: (error) => {
            message.error(getErrorMessage(error));
        },
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    // ── Preview Mode ─────────────────────────────────────────────
    if (disabled) {
        return (
            <div className="max-w-6xl mx-auto py-8 px-4">
                <h1 className="text-2xl font-semibold mb-1">Center Info</h1>
                <p className="text-sm text-gray-400 mb-6">You don't have permission to edit this information.</p>

                {logoIdPreview && (
                    <div className="mb-6">
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide block mb-2">Logo</span>
                        <img
                            src={getImg(logoIdPreview)}
                            alt="Center Logo"
                            className="max-h-24 rounded-xl border border-gray-100 object-contain"
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PreviewField label="Center Name (EN)" value={centerData?.name} />
                    <PreviewField label="Center Name (AR)" value={centerData?.nameAr} dir="rtl" />
                    <PreviewField label="Contact Phone"    value={centerData?.contactPhone} />
                    <PreviewField label="Contact Email"    value={centerData?.contactEmail} />
                    <PreviewField label="Address (EN)"     value={centerData?.address} />
                    <PreviewField label="Address (AR)"     value={centerData?.addressAr} dir="rtl" />
                    <PreviewField label="Location URL"     value={centerData?.locationUrl} />
                    <PreviewField label="Availability Note" value={centerData?.avilibiltyNote} />
                </div>

                {/* Social Media */}
                <h2 className="text-base font-semibold mt-8 mb-4 text-gray-700">Social Media</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {SOCIAL_FIELDS.map(({ name, label }) => (
                        <PreviewField key={name} label={label} value={centerData?.[name]} />
                    ))}
                </div>
            </div>
        );
    }

    // ── Edit Mode ────────────────────────────────────────────────
    return (
        <div className="max-w-6xl mx-auto py-8 px-4">
            <h1 className="text-2xl font-semibold mb-6">Edit Center</h1>

            <Form form={form} layout="vertical" onFinish={(values) => mutate(values)}>
                <Form.Item name="centerId" hidden>
                    <Input />
                </Form.Item>

                <div className="flex gap-4 w-full">
                    <Form.Item name="name" label="Center Name (EN)" className="flex-1">
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameAr" label="Center Name (AR)" className="flex-1">
                        <Input dir="rtl" />
                    </Form.Item>
                </div>

                <div className="flex gap-4 w-full">
                    <Form.Item name="contactPhone" label="Contact Phone" className="flex-1">
                        <Input />
                    </Form.Item>
                    <Form.Item name="contactEmail" label="Contact Email" className="flex-1">
                        <Input type="email" />
                    </Form.Item>
                </div>

                <div className="flex gap-4 w-full">
                    <Form.Item name="detailedAddress" label="Address (EN)" className="flex-1">
                        <Input />
                    </Form.Item>
                    <Form.Item name="detailedAddressAr" label="Address (AR)" className="flex-1">
                        <Input dir="rtl" />
                    </Form.Item>
                </div>

                <Form.Item name="locationUrl" label="Location URL">
                    <Input placeholder="https://maps.google.com/..." />
                </Form.Item>

                <Form.Item name="availabilityNote" label="Availability Note">
                    <Input />
                </Form.Item>

                {/* ── Social Media ── */}
                <p className="text-base font-semibold mb-2 text-gray-700">Social Media</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                    {SOCIAL_FIELDS.map(({ name, label, placeholder }) => (
                        <Form.Item
                            key={name}
                            name={name}
                            label={label}
                            rules={label=="linkedIn"?[
                                {
                                    type:"url",
                                    message: `Please enter a valid ${label} URL`,
                                },
                            ]:null}
                        >
                            <Input placeholder={placeholder} />
                        </Form.Item>
                    ))}
                </div>

                {/* ── Logo ── */}
                <Form.Item label="Center Logo">
                    <Upload
                        name="file"
                        accept=".jpg,.jpeg,.png"
                        showUploadList={false}
                        customRequest={async ({ file, onSuccess, onError }) => {
                            setUploading(true);
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                                const response = await drEasyApi.post(
                                    "/api/Storage/UploadImage",
                                    formData,
                                    { headers: { "Content-Type": "multipart/form-data" } }
                                );
                                const imageId = response.data.id;
                                form.setFieldsValue({ logoId: imageId });
                                setLogoIdPreview(imageId);
                                onSuccess();
                                message.success("Logo uploaded successfully");
                            } catch (error) {
                                message.error("Logo upload failed");
                                onError?.(error);
                            } finally {
                                setUploading(false);
                            }
                        }}
                    >
                        <Button icon={<UploadOutlined />} loading={uploading}>
                            Upload Logo
                        </Button>
                    </Upload>

                    {logoIdPreview && (
                        <img
                            src={getImg(logoIdPreview)}
                            alt="Center Logo"
                            className="mt-2 max-h-40 rounded-xl border border-gray-100 object-contain"
                        />
                    )}
                </Form.Item>

                <Form.Item name="logoId" hidden>
                    <Input />
                </Form.Item>

                <Form.Item>
                    <Button
                        size="large"
                        htmlType="submit"
                        loading={isPending}
                        disabled={uploading}
                        className="px-6 bg-primary text-white rounded hover:bg-primary_hover disabled:bg-gray-400 disabled:cursor-not-allowed w-full"
                    >
                        Save Changes
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default EditCenterScreen;